import { events, EVT } from '../utils/events.js';
import { REPAIR_TOOLS } from '../utils/constants.js';
import { censorName } from '../utils/profanity.js';
import { savePlanet, loadNextPlanetInChain, loadPlanetById } from '../firebase.js';

/**
 * Manages game state — astronaut identity, species journal, tool collection,
 * radio repair progress, and previous player's song.
 * Persists to localStorage.
 */
export class GameState {
  constructor() {
    // ── Player identity ──
    this.playerName = '';              // set during intro
    this.hasCompletedIntro = false;

    // ── Species journal ──
    // speciesId → { speciesId, name, bodyType, color, instrumentRole, listened: bool }
    this.speciesJournal = {};
    this.allSpeciesCount = 0;         // total species on this planet
    this.allSpeciesHeard = false;

    // ── Tool collection (for radio repair) ──
    this.collectedTools = [];          // tool id strings
    this.allToolsCollected = false;

    // ── Radio ──
    this.radioRepaired = false;

    // ── Story beats — drives the objective HUD line ──
    this.metKip = false;               // talked to KIP-3 at the crash site

    // ── Home planet — where the story sites (KIP-3, Song Circle) live ──
    this.homePlanetSeed = null;

    // ── Involuntary traces — shipped with your song so the next traveler
    //    feels a person (can't be griefed, unlike free text) ──
    this.stats = { playSeconds: 0, repairSeconds: null, savesCount: 0 };

    // ── Singing stars: other players' planets mapped into the galaxy ──
    // key 'gx,gy,planetIndex' → { id, playerName, songName, songData, planetMusic, stats }
    this.singingPlanets = new Map();
    this.singingSystems = new Set();  // 'gx,gy' keys for the galaxy map

    // ── Previous player's song (loaded from storage / server) ──
    this.previousPlayerName = '';
    this.previousSongName = '';
    this.previousSongData = null;      // { tracks: [...], bpm, key, ... }

    // ── Current player's composition ──
    this.mySongName = '';
    this.mySongData = null;

    // ── Legacy (kept for compatibility) ──
    this.claimedPlanets = [];
    this.collectedCreatures = [];
    this.currentSystem = { gx: 0, gy: 0 };
    this.currentPlanetIndex = 0;
    this.shipCreatures = [];
    this.resources = {};

    // ── Firebase remote planet (loaded on startup) ──
    this.remoteLastPlanet = null;
    this.previousPlanetMusic = null;   // planet.music config from the saved record

    this._load();
    this._bindEvents();
  }

  _bindEvents() {
    // ── Narrative events ──
    events.on(EVT.SPECIES_LISTENED, ({ speciesId }) => {
      if (this.speciesJournal[speciesId]) {
        this.speciesJournal[speciesId].listened = true;
        this._checkAllSpeciesHeard();
        this._save();
      }
    });

    events.on(EVT.TOOL_COLLECTED, ({ toolId }) => {
      if (!this.collectedTools.includes(toolId)) {
        this.collectedTools.push(toolId);
        this._checkAllTools();
        this._save();
      }
    });

    events.on(EVT.RADIO_REPAIRED, () => {
      this.radioRepaired = true;
      if (this.stats.repairSeconds == null) {
        this.stats.repairSeconds = Math.round(this.stats.playSeconds);
      }
      this._save();
    });

    events.on(EVT.MUSIC_MAKER_SAVE, (payload) => {
      const { songName, ...song } = payload;
      this.stats.savesCount++;
      this.mySongName = songName;
      this.mySongData = song; // full serialize() output — bars/octaves/loop included
      // Also store as the "previous player" for the next session
      this._savePreviousPlayerSong();
      this._save();
    });
  }

  // ── Intro ──

  setHomePlanet(seed) {
    this.homePlanetSeed = seed;
    this._save();
  }

  markKipMet() {
    if (this.metKip) return;
    this.metKip = true;
    this._save();
  }

  setPlayerName(name) {
    this.playerName = name;
    this.hasCompletedIntro = true;
    this._save();
  }

  // ── Species Journal ──

  initSpeciesJournal(allCreatures) {
    // Build journal from creature species — one entry per speciesId
    const seen = new Set();
    for (const c of allCreatures) {
      if (seen.has(c.speciesId)) continue;
      seen.add(c.speciesId);
      // Preserve listened state from a previous save
      const existing = this.speciesJournal[c.speciesId];
      this.speciesJournal[c.speciesId] = {
        speciesId: c.speciesId,
        name: c.name,
        bodyType: c.bodyType,
        color: c.color,
        instrumentRole: c.voice?.instrumentRole || 'unknown',
        listened: existing?.listened || false,
      };
    }
    this.allSpeciesCount = seen.size;
    this._checkAllSpeciesHeard();
    this._save();
  }

  getSpeciesEntry(speciesId) {
    return this.speciesJournal[speciesId] || null;
  }

  getListenedCount() {
    return Object.values(this.speciesJournal).filter(s => s.listened).length;
  }

  _checkAllSpeciesHeard() {
    const all = Object.values(this.speciesJournal);
    if (all.length > 0 && all.every(s => s.listened)) {
      if (!this.allSpeciesHeard) {
        this.allSpeciesHeard = true;
        events.emit(EVT.SPECIES_ALL_HEARD);
      }
    }
  }

  // ── Tools ──

  hasToolId(toolId) {
    return this.collectedTools.includes(toolId);
  }

  getToolProgress() {
    return {
      collected: this.collectedTools.length,
      total: REPAIR_TOOLS.length,
      tools: REPAIR_TOOLS.map(t => ({
        ...t,
        found: this.collectedTools.includes(t.id),
      })),
    };
  }

  _checkAllTools() {
    if (this.collectedTools.length >= REPAIR_TOOLS.length) {
      if (!this.allToolsCollected) {
        this.allToolsCollected = true;
        events.emit(EVT.ALL_TOOLS);
      }
    }
  }

  // ── Radio ──

  canRepairRadio() {
    return this.allToolsCollected && !this.radioRepaired;
  }

  // ── Previous player's song (localStorage stub — replace with server later) ──

  _savePreviousPlayerSong() {
    try {
      const data = {
        playerName: this.playerName,
        songName: this.mySongName,
        songData: this.mySongData,
      };
      localStorage.setItem('cosmic-symphony-prev-song', JSON.stringify(data));
    } catch (e) {
      console.warn('[GameState] Failed to save previous player song:', e);
    }
  }

  _loadPreviousPlayerSong() {
    try {
      const raw = localStorage.getItem('cosmic-symphony-prev-song');
      if (raw) {
        const data = JSON.parse(raw);
        this.previousPlayerName = censorName(data.playerName) || 'A Distant Traveler';
        this.previousSongName = censorName(data.songName) || 'Untitled Harmony';
        this.previousSongData = data.songData || null;
      }
    } catch (e) {
      console.warn('[GameState] Failed to load previous player song:', e);
    }
  }

  // ── Legacy compatibility ──

  isPlanetClaimed(gx, gy, planetIndex) {
    return this.claimedPlanets.some(
      p => p.systemGx === gx && p.systemGy === gy && p.planetIndex === planetIndex
    );
  }

  getPlanetClaimName(gx, gy, planetIndex) {
    const claim = this.claimedPlanets.find(
      p => p.systemGx === gx && p.systemGy === gy && p.planetIndex === planetIndex
    );
    return claim ? claim.name : null;
  }

  isCreatureCollected(creatureId) {
    return this.collectedCreatures.some(c => c.id === creatureId);
  }

  setLocation(gx, gy, planetIndex) {
    this.currentSystem = { gx, gy };
    this.currentPlanetIndex = planetIndex;
    this._save();
  }

  /* ── Singing stars — other players' planets in the galaxy ── */

  /**
   * Deterministically scatter fetched player planets across real systems
   * near the origin. Same doc id → same star for every player, so the map
   * of people is shared. Home cell (0,0) stays untouched.
   */
  assignSingingPlanets(docs, galaxy) {
    this.singingPlanets.clear();
    this.singingSystems.clear();
    const taken = new Set(['0,0']);

    const hash = (str) => {
      let h = 2166136261;
      for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return Math.abs(h);
    };

    for (const doc of docs || []) {
      const h = hash(doc.id || '');
      const tx = (h % 13) - 6;
      const ty = (Math.floor(h / 13) % 13) - 6;

      // Spiral out from the target cell to the nearest real, unclaimed system
      let system = null;
      outer:
      for (let r = 0; r <= 4; r++) {
        for (let dx = -r; dx <= r; dx++) {
          for (let dy = -r; dy <= r; dy++) {
            if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
            const sys = galaxy.getSystem(tx + dx, ty + dy);
            if (sys && !taken.has(`${sys.gx},${sys.gy}`)) { system = sys; break outer; }
          }
        }
      }
      if (!system) continue;
      taken.add(`${system.gx},${system.gy}`);

      const pIdx = h % system.planets.length;
      let songData = null, planetMusic = null, stats = null;
      try { songData = doc.songData ? JSON.parse(doc.songData) : null; } catch (_) {}
      try { planetMusic = doc.planetMusic ? JSON.parse(doc.planetMusic) : null; } catch (_) {}
      try { stats = doc.stats ? JSON.parse(doc.stats) : null; } catch (_) {}
      if (!songData?.tracks?.length) continue;

      this.singingPlanets.set(`${system.gx},${system.gy},${pIdx}`, {
        id: doc.id,
        // Other travelers' names arrive unmoderated — star out anything foul.
        playerName: censorName(doc.playerName) || 'A Distant Traveler',
        songName: censorName(doc.songName) || 'Untitled Harmony',
        songData, planetMusic, stats,
      });
      this.singingSystems.add(`${system.gx},${system.gy}`);
    }
    console.log(`[GameState] ${this.singingPlanets.size} singing stars mapped`);
  }

  getSingingSong(gx, gy, planetIndex) {
    return this.singingPlanets.get(`${gx},${gy},${planetIndex}`) || null;
  }

  isSingingSystem(gx, gy) {
    return this.singingSystems.has(`${gx},${gy}`);
  }

  getSingingForSystem(gx, gy) {
    for (const [key, val] of this.singingPlanets) {
      if (key.startsWith(`${gx},${gy},`)) return val;
    }
    return null;
  }

  /** Human line from a stats payload: the traveler's involuntary traces. */
  static statsLine(stats) {
    if (!stats) return '';
    const bits = [];
    if (stats.repairMinutes != null) bits.push(`repaired their radio after ${stats.repairMinutes || '<1'} min`);
    else if (stats.minutes) bits.push(`wandered ${stats.minutes} min`);
    if (stats.speciesHeard) bits.push(`heard ${stats.speciesHeard} species`);
    if (stats.drafts > 1) bits.push(`${stats.drafts} drafts`);
    return bits.join(' · ');
  }

  _save() {
    try {
      const data = {
        playerName: this.playerName,
        hasCompletedIntro: this.hasCompletedIntro,
        speciesJournal: this.speciesJournal,
        allSpeciesCount: this.allSpeciesCount,
        allSpeciesHeard: this.allSpeciesHeard,
        collectedTools: this.collectedTools,
        allToolsCollected: this.allToolsCollected,
        radioRepaired: this.radioRepaired,
        metKip: this.metKip,
        homePlanetSeed: this.homePlanetSeed,
        stats: this.stats,
        mySongName: this.mySongName,
        mySongData: this.mySongData,
        // Legacy
        claimedPlanets: this.claimedPlanets,
        collectedCreatures: this.collectedCreatures,
        currentSystem: this.currentSystem,
        currentPlanetIndex: this.currentPlanetIndex,
        shipCreatures: this.shipCreatures,
        resources: this.resources,
      };
      localStorage.setItem('cosmic-symphony-save', JSON.stringify(data));
    } catch (e) {
      console.warn('[GameState] Failed to save:', e);
    }
  }

  _load() {
    try {
      const raw = localStorage.getItem('cosmic-symphony-save');
      if (raw) {
        const data = JSON.parse(raw);
        // Carefully pick known fields to avoid overwriting methods
        this.playerName = data.playerName || '';
        this.hasCompletedIntro = data.hasCompletedIntro || false;
        this.speciesJournal = data.speciesJournal || {};
        this.allSpeciesCount = data.allSpeciesCount || 0;
        this.allSpeciesHeard = data.allSpeciesHeard || false;
        this.collectedTools = data.collectedTools || [];
        this.allToolsCollected = data.allToolsCollected || false;
        this.radioRepaired = data.radioRepaired || false;
        this.metKip = data.metKip || false;
        this.homePlanetSeed = data.homePlanetSeed ?? null;
        this.stats = data.stats || { playSeconds: 0, repairSeconds: null, savesCount: 0 };
        this.mySongName = data.mySongName || '';
        this.mySongData = data.mySongData || null;
        // Legacy
        this.claimedPlanets = data.claimedPlanets || [];
        this.collectedCreatures = data.collectedCreatures || [];
        this.currentSystem = data.currentSystem || { gx: 0, gy: 0 };
        this.currentPlanetIndex = data.currentPlanetIndex || 0;
        this.shipCreatures = data.shipCreatures || [];
        this.resources = data.resources || {};
        console.log('[GameState] Loaded save data');
      }
      this._loadPreviousPlayerSong();
    } catch (e) {
      console.warn('[GameState] Failed to load save:', e);
    }
  }

  reset() {
    localStorage.removeItem('cosmic-symphony-save');
    localStorage.removeItem('cosmic-symphony-prev-song');
    this.previousPlayerName = '';
    this.previousSongName = '';
    this.previousSongData = null;
    this.playerName = '';
    this.hasCompletedIntro = false;
    this.speciesJournal = {};
    this.allSpeciesCount = 0;
    this.allSpeciesHeard = false;
    this.collectedTools = [];
    this.allToolsCollected = false;
    this.radioRepaired = false;
    this.homePlanetSeed = null;
    this.stats = { playSeconds: 0, repairSeconds: null, savesCount: 0 };
    this.mySongName = '';
    this.mySongData = null;
    this.claimedPlanets = [];
    this.collectedCreatures = [];
    this.shipCreatures = [];
    this.resources = {};
    this.currentSystem = { gx: 0, gy: 0 };
    this.currentPlanetIndex = 0;
  }

  /**
   * Save a planet record to Firestore.
   * @param {string} planetName  - display name of the planet
   * @param {string} songName    - the song title
   * @param {object} songData    - full serialize() output: { tracks, bpm, key, scaleName }
   * @param {object} [planetMusic] - the planet.music config (key, scale, genre, tempo, etc.)
   * Returns the Firestore document id or null on failure.
   */
  async savePlanetToFirebase(planetName, songName, songData, planetMusic) {
    try {
      const record = {
        name: planetName || 'Unnamed Planet',
        position: {
          gx: this.currentSystem?.gx ?? 0,
          gy: this.currentSystem?.gy ?? 0,
          planetIndex: this.currentPlanetIndex ?? 0,
        },
        songName: songName || this.mySongName || '',
        // Human-readable chord/track list serialized as JSON
        chords: JSON.stringify(songData?.tracks || []),
        // Full song data for restoring the MusicMaker
        songData: JSON.stringify(songData || {}),
        // Planet music configuration so the next player's planet matches
        planetMusic: JSON.stringify(planetMusic || {}),
        playerName: this.playerName || '',
        stats: JSON.stringify({
          minutes: Math.round(this.stats.playSeconds / 60),
          repairMinutes: this.stats.repairSeconds != null ? Math.round(this.stats.repairSeconds / 60) : null,
          speciesHeard: this.getListenedCount(),
          drafts: this.stats.savesCount + 1,
        }),
      };
      const id = await savePlanet(record);
      console.log('[GameState] Planet saved to Firebase:', id);
      return id;
    } catch (e) {
      console.warn('[GameState] Failed to save planet to Firebase:', e);
      return null;
    }
  }

  /**
   * Load the most recently saved planet from Firestore.
   * Stores result in `this.remoteLastPlanet` and populates previous-player
   * song fields. The planet's creatures and music will be generated from
   * this data so the planet IS the previous player's composition.
   * Returns the raw document or null.
   */
  async loadPlanetFromFirebase() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const planetId = urlParams.get('planet');
      
      let data = null;
      if (planetId) {
        data = await loadPlanetById(planetId);
        if (!data) console.warn('[GameState] Planet ID not found, falling back to most recent.');
        // Strip ?planet= so a plain reload goes back to the normal flow.
        const cleanUrl = window.location.pathname + window.location.hash;
        history.replaceState(null, '', cleanUrl);
      }

      if (!data) {
        data = await loadNextPlanetInChain();
      }
      
      if (!data) return null;
      this.remoteLastPlanet = data;

      // ── Previous player's song — drives planet creature/music generation ──
      if (data.songData) {
        try { this.previousSongData = JSON.parse(data.songData); } catch (_) {
          if (data.chords) this.previousSongData = { tracks: JSON.parse(data.chords) };
        }
      } else if (data.chords) {
        this.previousSongData = { tracks: JSON.parse(data.chords) };
      }
      this.previousPlayerName = censorName(data.playerName) || 'A Distant Traveler';
      this.previousSongName = censorName(data.songName) || 'Untitled Harmony';

      // Restore the planet's music configuration if saved
      if (data.planetMusic) {
        try { this.previousPlanetMusic = JSON.parse(data.planetMusic); } catch (_) {}
      }

      console.log('[GameState] Loaded planet from Firebase:', data.name, '/', data.songName);
      return data;
    } catch (e) {
      console.warn('[GameState] Failed to load planet from Firebase:', e);
      return null;
    }
  }
}
